import { useState, useEffect } from 'react'
import {
  Search,
  Edit2,
  Trash2,
  Users,
  ShieldCheck,
  ShieldAlert,
  X,
  AlertCircle,
  RefreshCw,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  Eye
} from 'lucide-react'
import { Select } from '../components/ui/Select'
import { teamApi } from '../services/api'
import type {
  TeamMember,
  TeamMemberRole,
  TeamMemberStatus,
  TeamMetrics,
  RoleDefinition,
  DepartmentDefinition
} from '../services/api'

const formatNumber = (value: number, decimals: number = 1): string => {
  if (isNaN(value) || !isFinite(value)) return '0'
  return Number(value.toFixed(decimals)).toString()
}

export function TeamManagement() {
  const [members, setMembers] = useState<TeamMember[]>([])
  const [metrics, setMetrics] = useState<TeamMetrics | null>(null)
  const [roles, setRoles] = useState<RoleDefinition[]>([])
  const [departments, setDepartments] = useState<DepartmentDefinition[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterRole, setFilterRole] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'accountant' as TeamMemberRole,
    department: ''
  })
  const [submitting, setSubmitting] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [membersRes, metricsRes, rolesRes, deptsRes] = await Promise.all([
        teamApi.getMembers({
          role: filterRole !== 'all' ? filterRole as TeamMemberRole : undefined,
          status: filterStatus !== 'all' ? filterStatus as TeamMemberStatus : undefined,
          search: searchTerm || undefined
        }),
        teamApi.getMetrics(),
        teamApi.getRoles(),
        teamApi.getDepartments()
      ])
      setMembers(membersRes.members)
      setMetrics(metricsRes)
      setRoles(rolesRes)
      setDepartments(deptsRes)
    } catch (err: any) {
      console.error('Error fetching team data:', err)
      setError('Erro ao carregar dados da equipe')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [filterRole, filterStatus])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== '') fetchData()
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const handleAddMember = async () => {
    try {
      setSubmitting(true)
      await teamApi.createMember(formData)
      setShowAddModal(false)
      setFormData({ name: '', email: '', phone: '', role: 'accountant', department: departments[0]?.id || '' })
      fetchData()
    } catch (err: any) {
      setError(err.message || 'Erro ao adicionar membro')
    } finally {
      setSubmitting(false)
    }
  }

  const handleEditMember = async () => {
    if (!selectedMember) return
    try {
      setSubmitting(true)
      await teamApi.updateMember(selectedMember.id, formData)
      setShowEditModal(false)
      setSelectedMember(null)
      fetchData()
    } catch (err: any) {
      setError(err.message || 'Erro ao atualizar membro')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteMember = async () => {
    if (!selectedMember) return
    try {
      setSubmitting(true)
      await teamApi.deleteMember(selectedMember.id)
      setShowDeleteConfirm(false)
      setSelectedMember(null)
      fetchData()
    } catch (err: any) {
      setError(err.message || 'Erro ao remover membro')
    } finally {
      setSubmitting(false)
    }
  }

  const handleToggleStatus = async (member: TeamMember) => {
    try {
      if (member.status === 'active') {
        await teamApi.deactivateMember(member.id)
      } else {
        await teamApi.activateMember(member.id)
      }
      setOpenDropdown(null)
      fetchData()
    } catch (err: any) {
      setError(err.message || 'Erro ao alterar status')
    }
  }

  const openEditModal = (member: TeamMember) => {
    setSelectedMember(member)
    setFormData({
      name: member.name,
      email: member.email,
      phone: member.phone || '',
      role: member.role,
      department: member.department
    })
    setShowEditModal(true)
    setOpenDropdown(null)
  }



  const openDeleteConfirm = (member: TeamMember) => {
    setSelectedMember(member)
    setShowDeleteConfirm(true)
    setOpenDropdown(null)
  }

  const getRoleInfo = (role: TeamMemberRole) => {
    switch (role) {
      case 'admin': return { label: 'Administrador', color: 'bg-purple-100 text-purple-800' }
      case 'accountant': return { label: 'Contador', color: 'bg-blue-100 text-blue-800' }
      case 'assistant': return { label: 'Assistente', color: 'bg-green-100 text-green-800' }
      default: return { label: role, color: 'bg-gray-100 text-gray-800' }
    }
  }

  const getStatusInfo = (status: TeamMemberStatus) => {
    switch (status) {
      case 'active': return { label: 'Ativo', color: 'bg-green-100 text-green-800' }
      case 'inactive': return { label: 'Inativo', color: 'bg-gray-100 text-gray-600' }
      case 'pending_invitation': return { label: 'Pendente', color: 'bg-yellow-100 text-yellow-800' }
      default: return { label: status, color: 'bg-gray-100 text-gray-600' }
    }
  }

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
  }

  const filteredMembers = members.filter(member => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      return (
        member.name.toLowerCase().includes(search) ||
        member.email.toLowerCase().includes(search) ||
        member.department.toLowerCase().includes(search)
      )
    }
    return true
  })

  // Pagination calculations
  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedMembers = filteredMembers.slice(startIndex, startIndex + itemsPerPage)

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, filterRole, filterStatus])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="team-management">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-text">
          <h1 className="page-title">Gestão de Equipe</h1>
          <p className="page-subtitle">
            Gerencie os membros da sua equipe e suas permissões
          </p>
        </div>
        <div className="header-actions">
          <button
            onClick={fetchData}
            className="btn-secondary"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="btn-text">Atualizar</span>
          </button>
          <button
            onClick={() => {
              setFormData({ name: '', email: '', phone: '', role: 'accountant', department: departments[0]?.id || '' })
              setShowAddModal(true)
            }}
            className="btn-primary"
          >
            <UserPlus className="w-4 h-4" />
            <span className="btn-text">Novo Membro</span>
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="error-alert">
          <AlertCircle className="w-5 h-5" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} className="p-1 hover:bg-red-100 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}



      {/* Filters and Search */}
      <div className="filters-card">
        <div className="filters-row">
          <div className="search-container">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Buscar por nome, email ou departamento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="flex items-center gap-3">
            <Select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              options={[
                { value: 'all', label: 'Todos os Cargos' },
                ...roles.map((r) => ({ value: r.role, label: r.name }))
              ]}
            />

            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              options={[
                { value: 'all', label: 'Todos os Status' },
                { value: 'active', label: 'Ativo' },
                { value: 'inactive', label: 'Inativo' },
                { value: 'pending_invitation', label: 'Pendente' }
              ]}
            />


          </div>
        </div>
      </div>

      {/* Members Table View */}
      <div className="members-container">
        <div className="table-wrapper">
          <table className="members-table">
            <thead>
              <tr>
                <th>Membro</th>
                <th>Cargo</th>
                <th className="hidden-mobile">Departamento</th>
                <th>Status</th>
                <th className="hidden-mobile">Clientes</th>
                {/* Pós-MVP: MFA para membros da equipe - apenas visual, sem TOTP real implementado */}
                {/* <th className="hidden-mobile">MFA</th> */}
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {paginatedMembers.map((member) => {
                const roleInfo = getRoleInfo(member.role)
                const statusInfo = getStatusInfo(member.status)
                return (
                  <tr key={member.id}>
                    <td>
                      <div className="member-info">
                        <div className="member-name">{member.name}</div>
                        <div className="member-email">{member.email}</div>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${roleInfo.color}`}>
                        {roleInfo.label}
                      </span>
                    </td>
                    <td className="hidden-mobile">{member.department}</td>
                    <td>
                      <span className={`badge ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="hidden-mobile">{member.metrics.clientsManaged}</td>
                    {/* Pós-MVP: MFA para membros da equipe */}
                    {/* <td className="hidden-mobile">
                      {member.mfaEnabled ? (
                        <ShieldCheck className="w-5 h-5 text-green-600" />
                      ) : (
                        <ShieldAlert className="w-5 h-5 text-gray-400" />
                      )}
                    </td> */}
                    <td>
                      <div className="actions-cell">
                        <button
                          onClick={() => openEditModal(member)}
                          className="action-btn text-blue-600"
                          title="Ver detalhes"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(member)}
                          className="action-btn text-gray-600"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openDeleteConfirm(member)}
                          className="action-btn text-red-600"
                          title="Remover"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="mobile-cards">
          {paginatedMembers.map((member) => {
            const roleInfo = getRoleInfo(member.role)
            const statusInfo = getStatusInfo(member.status)
            return (
              <div key={member.id} className="member-card">
                <div className="card-header">
                  <div>
                    <h3 className="card-member-name">{member.name}</h3>
                    <p className="card-member-email">{member.email}</p>
                  </div>
                  <span className={`badge ${statusInfo.color}`}>
                    {statusInfo.label}
                  </span>
                </div>
                <div className="card-body">
                  <div className="card-row">
                    <span className="card-label">Cargo</span>
                    <span className={`badge ${roleInfo.color}`}>
                      {roleInfo.label}
                    </span>
                  </div>
                  <div className="card-row">
                    <span className="card-label">Departamento</span>
                    <span className="card-value">{member.department}</span>
                  </div>
                  <div className="card-row">
                    <span className="card-label">Clientes</span>
                    <span className="card-value font-semibold">{member.metrics.clientsManaged}</span>
                  </div>
                  <div className="card-row">
                    <span className="card-label">Tarefas</span>
                    <span className="card-value">{member.metrics.tasksCompleted}</span>
                  </div>
                </div>
                <div className="card-actions">
                  <button onClick={() => openEditModal(member)} className="card-action-btn">
                    <Eye className="w-4 h-4" />
                    <span>Ver</span>
                  </button>
                  <button onClick={() => openEditModal(member)} className="card-action-btn">
                    <Edit2 className="w-4 h-4" />
                    <span>Editar</span>
                  </button>
                  <button onClick={() => openDeleteConfirm(member)} className="card-action-btn text-red-600">
                    <Trash2 className="w-4 h-4" />
                    <span>Excluir</span>
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <div className="pagination-info">
              Mostrando {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredMembers.length)} de {filteredMembers.length}
            </div>
            <div className="pagination-controls">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="pagination-pages">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Empty State */}
      {filteredMembers.length === 0 && (
        <div className="empty-state">
          <Users className="empty-icon" />
          <h3 className="empty-title">Nenhum membro encontrado</h3>
          <p className="empty-text">
            Adicione um novo membro para começar a gerenciar sua equipe.
          </p>
          <button
            onClick={() => {
              setFormData({ name: '', email: '', phone: '', role: 'accountant', department: departments[0]?.id || '' })
              setShowAddModal(true)
            }}
            className="btn-primary mt-4"
          >
            <UserPlus className="w-4 h-4" />
            Adicionar Membro
          </button>
        </div>
      )}

      {/* Click outside to close dropdown */}
      {openDropdown && <div className="fixed inset-0 z-10" onClick={() => setOpenDropdown(null)} />}

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Novo Membro</h2>
              <button onClick={() => setShowAddModal(false)} className="modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Nome completo *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="form-input"
                  placeholder="Digite o nome"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="form-input"
                  placeholder="email@exemplo.com"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Telefone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="form-input"
                  placeholder="(11) 99999-9999"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Cargo</label>
                  <Select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as TeamMemberRole })}
                    options={roles.map((r) => ({ value: r.role, label: r.name }))}
                    fullWidth
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Departamento</label>
                  <Select
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    options={departments.map((dept) => ({ value: dept.id, label: dept.name }))}
                    fullWidth
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowAddModal(false)} className="btn-cancel">
                Cancelar
              </button>
              <button
                onClick={handleAddMember}
                disabled={submitting || !formData.name || !formData.email}
                className="btn-primary"
              >
                {submitting ? 'Adicionando...' : 'Adicionar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {showEditModal && selectedMember && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Editar Membro</h2>
              <button onClick={() => { setShowEditModal(false); setSelectedMember(null) }} className="modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Nome completo *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Telefone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Cargo</label>
                <Select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as TeamMemberRole })}
                  options={roles.map((r) => ({ value: r.role, label: r.name }))}
                  fullWidth
                />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => { setShowEditModal(false); setSelectedMember(null) }} className="btn-cancel">
                Cancelar
              </button>
              <button
                onClick={handleEditMember}
                disabled={submitting || !formData.name || !formData.email}
                className="btn-primary"
              >
                {submitting ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedMember && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '450px' }}>
            <div className="modal-header">
              <h2 className="modal-title text-red-600">Remover Membro</h2>
              <button onClick={() => { setShowDeleteConfirm(false); setSelectedMember(null) }} className="modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="modal-body">
              <div className="text-center py-4">
                <div className="delete-icon-wrapper">
                  <Trash2 className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="delete-confirm-title">Tem certeza que deseja remover?</h3>
                <p className="delete-confirm-text">
                  O membro <strong>{selectedMember.name}</strong> será removido permanentemente.
                  Esta ação não pode ser desfeita.
                </p>
              </div>
            </div>
            <div className="modal-footer justify-center gap-3">
              <button onClick={() => { setShowDeleteConfirm(false); setSelectedMember(null) }} className="btn-cancel">
                Cancelar
              </button>
              <button
                onClick={handleDeleteMember}
                disabled={submitting}
                className="btn-danger"
              >
                {submitting ? 'Removendo...' : 'Sim, Remover'}
              </button>
            </div>
          </div>
        </div>
      )}



      <style>{`
        .team-management {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        /* Page Header */
        .page-header {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        @media (min-width: 640px) {
          .page-header {
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
          }
        }

        .header-text {
          flex: 1;
        }

        .page-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #111827;
          margin: 0;
        }

        :root.dark .page-title {
          color: #f9fafb;
        }

        @media (min-width: 640px) {
          .page-title {
            font-size: 1.5rem;
          }
        }

        .page-subtitle {
          color: #6b7280;
          margin-top: 0.25rem;
          font-size: 0.875rem;
        }

        :root.dark .page-subtitle {
          color: #9ca3af;
        }

        .header-actions {
          display: flex;
          gap: 0.5rem;
          width: 100%;
        }

        @media (min-width: 640px) {
          .header-actions {
            width: auto;
          }
        }

        /* Buttons */
        .btn-primary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          background: #2563eb;
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-weight: 500;
          font-size: 0.875rem;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
          flex: 1;
        }

        @media (min-width: 640px) {
          .btn-primary {
            flex: none;
          }
        }

        .btn-primary:hover {
          background: #1d4ed8;
        }

        .btn-primary:disabled {
          background: #93c5fd;
          cursor: not-allowed;
        }

        .btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          background: #f3f4f6;
          color: #374151;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-weight: 500;
          font-size: 0.875rem;
          border: 1px solid #e5e7eb;
          cursor: pointer;
          transition: all 0.2s;
          flex: 1;
        }

        :root.dark .btn-secondary {
          background: #374151;
          color: #f3f4f6;
          border-color: #4b5563;
        }

        @media (min-width: 640px) {
          .btn-secondary {
            flex: none;
          }
        }

        .btn-secondary:hover {
          background: #e5e7eb;
        }

        :root.dark .btn-secondary:hover {
          background: #4b5563;
        }

        .btn-text {
          display: none;
        }

        @media (min-width: 480px) {
          .btn-text {
            display: inline;
          }
        }

        .btn-cancel {
          padding: 0.5rem 1rem;
          color: #374151;
          font-weight: 500;
          cursor: pointer;
          background: transparent;
          border: none;
        }

        .btn-cancel:hover {
          color: #111827;
        }

        .btn-danger {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          background: #dc2626;
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-weight: 500;
          font-size: 0.875rem;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-danger:hover {
          background: #b91c1c;
        }

        .btn-danger:disabled {
          background: #f87171;
          cursor: not-allowed;
        }

        /* Error Alert */
        .error-alert {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          font-size: 0.875rem;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
        }

        @media (min-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 1rem;
          }
        }

        .stats-grid-2 {
          grid-template-columns: repeat(2, 1fr);
        }

        @media (min-width: 768px) {
          .stats-grid-2 {
            grid-template-columns: repeat(2, 1fr);
            max-width: 500px;
          }
        }

        .stat-card {
          background: white;
          padding: 1rem;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
        }

        @media (min-width: 640px) {
          .stat-card {
            padding: 1.5rem;
          }
        }

        .stat-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .stat-info {
          min-width: 0;
        }

        .stat-label {
          font-size: 0.75rem;
          font-weight: 500;
          color: #6b7280;
          margin: 0;
        }

        @media (min-width: 640px) {
          .stat-label {
            font-size: 0.875rem;
          }
        }

        .stat-value {
          font-size: 1.25rem;
          font-weight: 700;
          color: #111827;
          margin: 0;
          margin-top: 0.25rem;
        }

        @media (min-width: 640px) {
          .stat-value {
            font-size: 1.5rem;
          }
        }

        .stat-icon {
          width: 24px;
          height: 24px;
          flex-shrink: 0;
        }

        @media (min-width: 640px) {
          .stat-icon {
            width: 32px;
            height: 32px;
          }
        }

        /* Performers Section */
        .performers-section {
          margin-top: 0.5rem;
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
        }

        .section-title {
          font-size: 1rem;
          font-weight: 600;
          color: #111827;
          margin: 0;
        }

        .performers-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.75rem;
        }

        @media (min-width: 640px) {
          .performers-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        .performer-card {
          border-radius: 12px;
          padding: 1rem;
          border: 1px solid #e5e7eb;
        }

        .performer-gold {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          border-color: #fcd34d;
        }

        .performer-silver {
          background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
          border-color: #d1d5db;
        }

        .performer-bronze {
          background: linear-gradient(135deg, #fed7aa 0%, #fdba74 100%);
          border-color: #fb923c;
        }

        .performer-content {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .performer-avatar {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 0.875rem;
          flex-shrink: 0;
        }

        .performer-info {
          flex: 1;
          min-width: 0;
        }

        .performer-rank {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .medal {
          font-size: 1rem;
        }

        .rank-number {
          font-size: 0.75rem;
          color: #6b7280;
        }

        .performer-name {
          font-weight: 600;
          color: #111827;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .performer-role {
          font-size: 0.75rem;
          color: #6b7280;
          margin: 0;
        }

        .performer-score {
          text-align: right;
        }

        .score-value {
          font-size: 1.25rem;
          font-weight: 700;
          color: #111827;
          margin: 0;
        }

        .score-label {
          font-size: 0.75rem;
          color: #6b7280;
          margin: 0;
        }

        /* Filters */
        .filters-card {
          background: white;
          padding: 1rem;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
        }

        :root.dark .filters-card {
          background: #1f2937;
          border-color: #374151;
        }

        @media (min-width: 640px) {
          .filters-card {
            padding: 1.5rem;
          }
        }

        .filters-row {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        @media (min-width: 640px) {
          .filters-row {
            flex-direction: row;
            gap: 1rem;
          }
        }

        .search-container {
          position: relative;
          flex: 1;
        }

        .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          width: 16px;
          height: 16px;
          color: #9ca3af;
        }

        .search-input {
          width: 100%;
          padding: 0.5rem 0.75rem 0.5rem 2.5rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.875rem;
          background: white;
          color: #111827;
        }

        :root.dark .search-input {
          background: #374151;
          border-color: #4b5563;
          color: #f9fafb;
        }

        :root.dark .search-input::placeholder {
          color: #9ca3af;
        }

        .search-input:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        .filter-select {
          padding: 0.5rem 2rem 0.5rem 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.875rem;
          background: white;
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
          background-position: right 0.5rem center;
          background-repeat: no-repeat;
          background-size: 1.5em 1.5em;
        }

        .filter-select:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        .view-toggle {
          display: none;
          background: #f3f4f6;
          border-radius: 8px;
          padding: 0.25rem;
        }

        @media (min-width: 640px) {
          .view-toggle {
            display: flex;
          }
        }

        .view-btn {
          padding: 0.5rem;
          border-radius: 6px;
          border: none;
          background: transparent;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s;
        }

        .view-btn:hover {
          color: #374151;
        }

        .view-btn.active {
          background: white;
          color: #2563eb;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        /* Members Container */
        .members-container {
          background: white;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          overflow: hidden;
        }

        :root.dark .members-container {
          background: #1f2937;
          border-color: #374151;
        }

        /* Table */
        .table-wrapper {
          display: none;
          overflow-x: auto;
        }

        @media (min-width: 768px) {
          .table-wrapper {
            display: block;
          }
        }

        .members-table {
          width: 100%;
          border-collapse: collapse;
        }

        .members-table th {
          padding: 0.75rem 1rem;
          text-align: left;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #6b7280;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          white-space: nowrap;
        }

        :root.dark .members-table th {
          background: #111827;
          color: #9ca3af;
          border-bottom-color: #374151;
        }

        .members-table td {
          padding: 1rem;
          border-bottom: 1px solid #e5e7eb;
          font-size: 0.875rem;
          color: #111827;
        }

        :root.dark .members-table td {
          color: #f3f4f6;
          border-bottom-color: #374151;
        }

        .members-table tbody tr:hover {
          background: #f9fafb;
        }

        :root.dark .members-table tbody tr:hover {
          background: #374151;
        }

        .hidden-mobile {
          display: none;
        }

        @media (min-width: 1024px) {
          .hidden-mobile {
            display: table-cell;
          }
        }

        .member-info {
          min-width: 200px;
        }

        .member-name {
          font-weight: 500;
          color: #111827;
        }

        :root.dark .member-name {
          color: #f9fafb;
        }

        .member-email {
          font-size: 0.875rem;
          color: #6b7280;
        }

        :root.dark .member-email {
          color: #9ca3af;
        }

        .badge {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .actions-cell {
          display: flex;
          gap: 0.5rem;
        }

        .action-btn {
          padding: 0.5rem;
          border: none;
          background: transparent;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.2s;
        }

        .action-btn:hover {
          background: #f3f4f6;
        }

        /* Mobile Cards */
        .mobile-cards {
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        @media (min-width: 768px) {
          .mobile-cards {
            display: none;
          }
        }

        .member-card {
          border-bottom: 1px solid #e5e7eb;
          padding: 1rem;
        }

        :root.dark .member-card {
          border-bottom-color: #374151;
        }

        .member-card:last-child {
          border-bottom: none;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.75rem;
        }

        .card-member-name {
          font-weight: 600;
          color: #111827;
          margin: 0;
          font-size: 0.9375rem;
        }

        :root.dark .card-member-name {
          color: #f9fafb;
        }

        .card-member-email {
          font-size: 0.8125rem;
          color: #6b7280;
          margin: 0;
          margin-top: 0.125rem;
        }

        :root.dark .card-member-email {
          color: #9ca3af;
        }

        .card-body {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .card-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.8125rem;
        }

        .card-label {
          color: #6b7280;
        }

        :root.dark .card-label {
          color: #9ca3af;
        }

        .card-value {
          color: #111827;
        }

        :root.dark .card-value {
          color: #f3f4f6;
        }

        .card-actions {
          display: flex;
          gap: 0.5rem;
          margin-top: 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px solid #f3f4f6;
        }

        :root.dark .card-actions {
          border-top-color: #374151;
        }

        .card-action-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.375rem;
          padding: 0.5rem;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-size: 0.75rem;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s;
        }

        :root.dark .card-action-btn {
          background: #374151;
          border-color: #4b5563;
          color: #d1d5db;
        }

        .card-action-btn:hover {
          background: #f3f4f6;
        }

        :root.dark .card-action-btn:hover {
          background: #4b5563;
        }

        .card-action-btn.text-red-600 {
          color: #dc2626;
        }

        /* Grid View */
        .members-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
        }

        @media (min-width: 640px) {
          .members-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (min-width: 1024px) {
          .members-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        .member-card-grid {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          overflow: hidden;
        }

        .card-grid-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          padding: 1rem;
          background: #f9fafb;
        }

        .card-grid-body {
          padding: 1rem;
        }

        .card-grid-name {
          font-weight: 600;
          color: #111827;
          margin: 0 0 0.25rem 0;
        }

        .card-grid-email {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0 0 0.75rem 0;
        }

        .card-grid-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .card-grid-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.5rem;
          padding-top: 1rem;
          border-top: 1px solid #e5e7eb;
        }

        .stat-item {
          text-align: center;
        }

        .stat-number {
          display: block;
          font-size: 1.125rem;
          font-weight: 700;
          color: #111827;
        }

        .stat-text {
          display: block;
          font-size: 0.75rem;
          color: #6b7280;
        }

        .card-grid-actions {
          display: flex;
          border-top: 1px solid #e5e7eb;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 3rem 1rem;
          background: white;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
        }

        :root.dark .empty-state {
          background: #1f2937;
          border-color: #374151;
        }

        .empty-icon {
          width: 48px;
          height: 48px;
          color: #9ca3af;
          margin: 0 auto 1rem;
        }

        :root.dark .empty-icon {
          color: #6b7280;
        }

        .empty-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #111827;
          margin: 0 0 0.5rem 0;
        }

        :root.dark .empty-title {
          color: #f9fafb;
        }

        .empty-text {
          color: #6b7280;
          margin: 0;
        }

        :root.dark .empty-text {
          color: #9ca3af;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          z-index: 100;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 500px;
          max-height: calc(100vh - 2rem);
          overflow-y: auto;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }

        :root.dark .modal-content {
          background: #1f2937;
          border: 1px solid #374151;
        }

        @media (max-width: 480px) {
          .modal-content {
            border-radius: 12px 12px 0 0;
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            max-height: 90vh;
          }

          .modal-overlay {
            align-items: flex-end;
            padding: 0;
          }
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #e5e7eb;
        }

        :root.dark .modal-header {
          border-bottom-color: #374151;
        }

        .modal-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #111827;
          margin: 0;
        }

        :root.dark .modal-title {
          color: #f9fafb;
        }

        .modal-close {
          padding: 0.5rem;
          border: none;
          background: transparent;
          color: #6b7280;
          cursor: pointer;
          border-radius: 6px;
        }

        .modal-close:hover {
          background: #f3f4f6;
          color: #111827;
        }

        .modal-body {
          padding: 1.5rem;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        :root.dark .modal-footer {
          border-top-color: #374151;
          background: #111827;
        }

        /* Form */
        .form-group {
          margin-bottom: 1rem;
        }

        .form-row {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        .form-label {
          display: block;
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.375rem;
        }

        :root.dark .form-label {
          color: #d1d5db;
        }

        .form-input {
          width: 100%;
          padding: 0.625rem 0.875rem;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.875rem;
          transition: all 0.2s;
          background: white;
          color: #111827;
        }

        :root.dark .form-input {
          background: #374151;
          border-color: #4b5563;
          color: #f9fafb;
        }

        :root.dark .form-input::placeholder {
          color: #9ca3af;
        }

        .form-input:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        /* Delete Confirmation */
        .delete-icon-wrapper {
          width: 64px;
          height: 64px;
          background: #fef2f2;
          border-radius: 9999px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
        }

        :root.dark .delete-icon-wrapper {
          background: rgba(220, 38, 38, 0.2);
        }

        .delete-confirm-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #111827;
          margin: 0 0 0.5rem 0;
        }

        :root.dark .delete-confirm-title {
          color: #f9fafb;
        }

        .delete-confirm-text {
          color: #6b7280;
          font-size: 0.875rem;
          margin: 0;
        }

        :root.dark .delete-confirm-text {
          color: #9ca3af;
        }

        /* Pagination */
        .pagination {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          border-top: 1px solid #e5e7eb;
        }

        :root.dark .pagination {
          border-top-color: #374151;
        }

        @media (min-width: 640px) {
          .pagination {
            flex-direction: row;
            justify-content: space-between;
          }
        }

        .pagination-info {
          font-size: 0.875rem;
          color: #6b7280;
        }

        :root.dark .pagination-info {
          color: #9ca3af;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .pagination-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s;
        }

        :root.dark .pagination-btn {
          background: #374151;
          border-color: #4b5563;
          color: #d1d5db;
        }

        .pagination-btn:hover:not(:disabled) {
          background: #f3f4f6;
          border-color: #9ca3af;
        }

        :root.dark .pagination-btn:hover:not(:disabled) {
          background: #4b5563;
          border-color: #6b7280;
        }

        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-pages {
          font-size: 0.875rem;
          color: #374151;
          padding: 0 0.5rem;
        }

        :root.dark .pagination-pages {
          color: #d1d5db;
        }
      `}</style>
    </div>
  )
}
